// Upgrading @atlaskit/platform; fix these violations.
// eslint-disable-next-line @compiled/jsx-pragma
/** @jsxImportSource @compiled/react */
import React from 'react';
import { css, styled } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled2 from 'styled-components';
import * as fonts from '@helpcenter/styles/src/fonts';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Label, RequiredAsterisk } from '@atlaskit/form';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useIsCSMTypographyMigrationEnabled } from '@atlassian/help-center-common-util/enable-typography-migration';
import { FieldDescription } from './field-description';

const ERROR_MESSAGE_ICON_A11Y_ISSUE = 'error_message_icon_a11y_issue';

const isErrorMessageIconA11yIssueEnabled = (): boolean =>
	FeatureGates.checkGate(ERROR_MESSAGE_ICON_A11Y_ISSUE);

export interface FieldBaseProps {
	label: string | React.ReactNode;
	labelId?: string;
	htmlFor: string;
	errorMessage?: React.ReactNode;
	description?: string | React.ReactNode;
	required?: boolean;
	children?: React.ReactNode;
	noLabel?: boolean;
	tooltip?: React.ReactNode;
}

const FieldBase: React.FC<FieldBaseProps> = ({
	label,
	labelId,
	required,
	errorMessage,
	description,
	children,
	noLabel,
	htmlFor,
	tooltip,
}) => {
	const isCSMTypographyMigrationEnabled = useIsCSMTypographyMigrationEnabled();
	const isErrorMessageIconEnabled = isErrorMessageIconA11yIssueEnabled();
	const renderField = () =>
		noLabel ? (
			children
		) : (
			<>
				{/* The old label had a margingTop of 20px but Field has marginTop of 8px hence this FieldContainer is required */}
				<FieldContainer>
					<Label htmlFor={htmlFor} id={labelId}>
						{label}
						{required && (
							<>
								<RequiredAsterisk />
								{isCSMTypographyMigrationEnabled ? (
									<div css={requireMessageCSS}>
										{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
										<Text>(required)</Text>
									</div>
								) : (
									// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
									<RequiredMessage>(required)</RequiredMessage>
								)}
							</>
						)}
						{tooltip && <>{tooltip}</>}
					</Label>
				</FieldContainer>
				{children}
			</>
		);

	return (
		<div data-test-id={`request-field-${htmlFor}`}>
			{renderField()}
			{!!errorMessage &&
				(isCSMTypographyMigrationEnabled ? (
					<div css={errorMessageCSS}>
						<Text
							size="small"
							color="color.text.danger"
							testId="field-error-text"
							id="field-error"
							role="status"
							aria-live="assertive"
						>
							{isErrorMessageIconEnabled && (
								<div css={errorIconWrapperCSS}>
									<StatusErrorIcon
										label="error"
										size="small"
										color={token('color.text.danger', '#AE2E24')}
									/>
								</div>
							)}
							{errorMessage}
						</Text>
					</div>
				) : (
					<>
						<ErrorText
							data-test-id="field-error"
							id="field-error"
							role="status"
							aria-live="assertive"
						>
							{isErrorMessageIconEnabled && (
								<div css={errorIconWrapperCSS}>
									<StatusErrorIcon
										label="error"
										size="small"
										color={token('color.text.danger', '#AE2E24')}
									/>
								</div>
							)}
							{errorMessage}
						</ErrorText>
					</>
				))}
			{description && (
				<FieldDescription id={`${htmlFor}-field-description`}>{description}</FieldDescription>
			)}
		</div>
	);
};

export default FieldBase;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ErrorText = styled2.p`
    ${fonts.small};
    margin-top: ${token('space.050', '4px')};
    color: ${token('color.text.danger', '#BF2600')};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const FieldContainer = styled.div({
	marginTop: token('space.200', '16px'),
	paddingTop: token('space.025', '2px'),
});

const errorIconWrapperCSS = css({
	marginRight: token('space.050', '4px'),
	display: 'inline-flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const RequiredMessage = styled.span({
	clip: 'rect(1px, 1px, 1px, 1px)',
	height: '1px',
	overflow: 'hidden',
	position: 'absolute',
	whiteSpace: 'nowrap',
	width: '1px',
	'&:focus': {
		clip: 'auto',
		height: 'auto',
		overflow: 'auto',
		position: 'absolute',
		width: 'auto',
	},
});

const requireMessageCSS = css({
	clip: 'rect(1px, 1px, 1px, 1px)',
	height: '1px',
	overflow: 'hidden',
	position: 'absolute',
	whiteSpace: 'nowrap',
	width: '1px',
	'&:focus': {
		clip: 'auto',
		height: 'auto',
		overflow: 'auto',
		position: 'absolute',
		width: 'auto',
	},
});

const errorMessageCSS = css({
	marginTop: token('space.050', '4px'),
	color: token('color.text.danger', '#AE2E24'),
});
